/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { accessAPI, NavLateral, usePermiso } from "../../../shared/index.js";
import { useContextoAsistente } from "../../asistente/ContextoAsistente";

/**
 * Layout para la sección de detalle del cliente. Monta la botonera lateral
 * de navegación (información / procesos / facturas / configuración) y un
 * Outlet donde se renderiza la subruta activa. Cada subruta se encarga de
 * cargar los datos que necesita.
 *
 * El layout sólo consulta las propuestas pendientes y hallazgos abiertos
 * para mostrar el badge de pendientes en el botón de procesos.
 */
export default function ClienteLayout() {
  const { clienteId } = useParams();
  // El layout no carga el cliente: va sin nombre y el asistente lo busca por id.
  useContextoAsistente({ tipo: "cliente", id: clienteId });
  const [propuestasPendientesCount, setPropuestasPendientesCount] = useState(0);
  const [hallazgosAbiertosCount, setHallazgosAbiertosCount] = useState(0);
  const [facturasPendientesCount, setFacturasPendientesCount] = useState(0);
  const cantidadPendientes = propuestasPendientesCount + hallazgosAbiertosCount;
  // Sin permiso de facturación, la sección Facturas no aparece ni se consulta
  const { puedeVer: puedeVerFacturacion } = usePermiso("admin.facturacion");

  useEffect(() => {
    if (!clienteId) return;
    accessAPI(
      "GET",
      `admin/hallazgo/cliente/${clienteId}`,
      null,
      (respuesta) => setHallazgosAbiertosCount(respuesta.length),
      () => {}
    );
  }, [clienteId]);

  // Propuestas y facturas entran en el permiso de facturación
  useEffect(() => {
    if (!clienteId || !puedeVerFacturacion) return;
    accessAPI(
      "GET",
      `admin/propuesta/cliente/${clienteId}`,
      null,
      (respuesta) => {
        setPropuestasPendientesCount(
          respuesta.filter(
            (p) => p.fechaemision && !p.fechaaceptacion && !p.archivada
          ).length
        );
      },
      () => {}
    );
    accessAPI(
      "GET",
      `admin/facturacion/cliente/${clienteId}`,
      null,
      (respuesta) =>
        setFacturasPendientesCount(
          respuesta.filter(
            (f) => !f.fechaPago && !f.anulada
          ).length
        ),
      () => {}
    );
  }, [clienteId, puedeVerFacturacion]);

  const navItems = [
    {
      icono: "pi pi-info-circle",
      etiqueta: "Información",
      to: `/clientes/${clienteId}/informacion`
    },
    {
      icono: "pi pi-sitemap",
      etiqueta: "Procesos",
      to: `/clientes/${clienteId}/procesos`,
      badge: cantidadPendientes > 0 ? String(cantidadPendientes) : null
    },
    {
      icono: "pi pi-map-marker",
      etiqueta: "Sitios",
      to: `/clientes/${clienteId}/sitios`
    },
    ...(puedeVerFacturacion
      ? [
          {
            icono: "pi pi-receipt",
            etiqueta: "Facturas",
            to: `/clientes/${clienteId}/facturas`,
            badge:
              facturasPendientesCount > 0
                ? String(facturasPendientesCount)
                : null
          }
        ]
      : []),
    {
      icono: "pi pi-cog",
      etiqueta: "Configuración",
      to: `/clientes/${clienteId}/configuracion`
    }
  ];

  return (
    <div
      css={{
        // Espacio abajo para no quedar debajo de la botonera mobile
        "@media (max-width: 767px)": {
          paddingBottom: "5em"
        }
      }}
    >
      <NavLateral items={navItems} />
      <Outlet />
    </div>
  );
}
